import { Modal } from "@mixitone/components";
import { TournamentPaymentMethod } from "@mixitone/models";
import { ApplicationView } from "@mixitone/mvc";
import { ValidationError } from "@mixitone/oom";
import {
  AKAHU_AMOUNT_OVER_LIMIT,
  AKAHU_ONE_OFF_MAX_AMOUNT_DEFAULT,
  getAkahuClubSetupCopy,
} from "../../../../payments/src/akahu/copy";
import { isMfaSessionAbortError, requireMfaSession } from "lib/mfa/requireMfaSession";
import { createSupabase } from "lib/supabaseClient";
import { messagesService } from "services";
import { getPaymentMethod } from "../../../../payments/src/methods";
import type { AkahuOneOff } from "../../../../payments/src/methods/AkahuOneOff";
import ClubAkahuSetupBanner from "./ClubAkahuSetupBanner";
import Form from "./Form";
import FormButtons from "./FormButtons";
import { FormController } from "./FormController";
import type { FormFieldConfig } from "./FormFieldsDefinition";
import { ModalFormFieldset } from "./FormRenderers/ModalFormRenderer";
import { TournamentSessionFormController } from "./TournamentSessionForm";

interface PaymentMethodAttributes {
  tournament_session_id?: string;
  payment_type?: string;
  description?: string;
  instructions?: string;
}

interface PaymentMethodProps {
  paymentMethod?: TournamentPaymentMethod;
  tournament_session_id: string;
  payment_type?: string;
  onClose: () => void;
  onSave: (paymentMethod: TournamentPaymentMethod) => void;
}

interface TournamentPaymentMethodFormState {
  configFields: FormFieldConfig[];
  akahuMaxAmount: number;
}

export class TournamentPaymentMethodFormController extends FormController<
  TournamentPaymentMethod,
  PaymentMethodAttributes,
  TournamentPaymentMethodFormState,
  PaymentMethodProps
> {
  static override initialState = {
    ...FormController.initialState,
    configFields: [] as FormFieldConfig[],
    akahuMaxAmount: AKAHU_ONE_OFF_MAX_AMOUNT_DEFAULT,
  };

  override async buildRecord(): Promise<TournamentPaymentMethod> {
    return new TournamentPaymentMethod({
      ...(this.props.attributes || {}),
      description: getPaymentMethod(this.props.payment_type!)?.getDescription() || "",
      tournament_session_id: this.props.tournament_session_id,
      payment_type: this.props.payment_type || "",
      is_enabled: true,
      is_deleted: false,
      configuration: {},
    });
  }

  override async findModel(id: string) {
    if (this.props.paymentMethod) return this.props.paymentMethod;
    return TournamentPaymentMethod.query().find(id);
  }

  private async loadAkahuMaxAmount(): Promise<number> {
    try {
      const supabase = createSupabase();
      const { data, error } = await supabase.rpc("get_akahu_one_off_max_amount");
      if (error || data == null) {
        return AKAHU_ONE_OFF_MAX_AMOUNT_DEFAULT;
      }
      const parsed = Number(data);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : AKAHU_ONE_OFF_MAX_AMOUNT_DEFAULT;
    } catch {
      return AKAHU_ONE_OFF_MAX_AMOUNT_DEFAULT;
    }
  }

  private buildConfigFields(paymentType: string, maxAmount: number): FormFieldConfig[] {
    const paymentMethod = getPaymentMethod(paymentType);
    if (!paymentMethod) return [];

    const fields =
      paymentType === "akahu-one-off"
        ? (paymentMethod as AkahuOneOff).getConfigFields({ maxAmount })
        : paymentMethod.getConfigFields();

    return fields.map((field) => {
      return {
        ...field,
        onChange: (value: any) => {
          this.state.record.configuration[field.name] = value;
        },
        value: () => this.state.record.configuration[field.name],
      };
    });
  }

  override async onLoad() {
    await super.onLoad();

    this.onSubmit = async () => {
      this.props.onClose();
    };

    if (this.state.record.payment_type === "akahu-one-off") {
      this.state.akahuMaxAmount = await this.loadAkahuMaxAmount();
    }

    if (this.state.record.payment_type) {
      this.state.configFields = this.buildConfigFields(
        this.state.record.payment_type,
        this.state.akahuMaxAmount,
      );
    }
  }

  private assertAkahuClubReady() {
    try {
      const session = this.get(TournamentSessionFormController);
      if (session.needsAkahuConnection) {
        const message = getAkahuClubSetupCopy(session.hasAkahuEnduring).notReadyToSave;
        messagesService.addMessage(message, "error");
        throw new ValidationError({
          akahu: message,
        });
      }
    } catch (error) {
      if (error instanceof ValidationError) throw error;
      // Session scope missing: skip client gate; server still enforces where applicable.
    }
  }

  override async saveRecord(paymentMethod: TournamentPaymentMethod) {
    if (paymentMethod.payment_type === "akahu-one-off") {
      paymentMethod.instructions = "";
      this.assertAkahuClubReady();

      const amount = Number(paymentMethod.configuration?.amount);
      const maxAmount = this.state.akahuMaxAmount;
      if (!Number.isFinite(amount) || amount <= 0) {
        messagesService.addMessage("Enter a valid pay by bank amount", "error");
        throw new ValidationError({ amount: "Enter a valid pay by bank amount" });
      }
      if (amount > maxAmount) {
        const message = AKAHU_AMOUNT_OVER_LIMIT(maxAmount);
        messagesService.addMessage(message, "error");
        throw new ValidationError({ amount: message });
      }

      try {
        await requireMfaSession({
          reason: "Confirm with your authenticator app to save this pay by bank payment method.",
          returnPath: this.props.tournament_session_id
            ? `/tournament/${this.props.tournament_session_id}`
            : undefined,
        });
      } catch (error) {
        if (!isMfaSessionAbortError(error)) {
          messagesService.addMessage(
            error instanceof Error ? error.message : "Two-factor authentication failed",
            "error",
          );
        }
        throw error;
      }
    }
    paymentMethod.tournament_session_id = this.props.tournament_session_id;
    try {
      await paymentMethod.save();
    } catch (error) {
      const message = error instanceof Error ? error.message : "";
      if (message.includes("Pay by bank amount cannot exceed")) {
        messagesService.addMessage(message, "error");
        throw new ValidationError({ amount: message });
      }
      throw error;
    }
    if (this.props.onSave) {
      this.props.onSave(paymentMethod);
    }
  }

  actionCancel() {
    if (this.props.onClose) {
      this.props.onClose();
    }
  }

  override fields() {
    const isAkahu = (this.state.record?.payment_type || this.props.payment_type) === "akahu-one-off";
    const fields: Record<string, FormFieldConfig> = {
      description: {
        name: "description",
        type: "text",
        label: "Description",
        required: true,
        group: "basic",
      },
    };
    if (!isAkahu) {
      fields.instructions = {
        name: "instructions",
        type: "text",
        label: "Instructions",
        group: "basic",
      };
    }
    return fields;
  }
}

const TournamentPaymentMethodForm: React.FC<PaymentMethodProps & { id?: string }> = (props) => {
  const controller = TournamentPaymentMethodFormController.use();
  const sessionController = TournamentSessionFormController.use();
  const { configFields, loading, record, newRecord } = controller.state;
  const { clubAkahuConnection, loadingClubAkahuConnection, startingAkahuOnboarding } =
    sessionController.state;
  const basicFields = Object.values(controller.fields()).filter((field) => field.group === "basic");
  const title = newRecord ? "Add payment method" : "Edit payment method";
  const paymentType = record?.payment_type || props.payment_type;
  const isAkahu = paymentType === "akahu-one-off";
  const akahuBlocked = isAkahu && (loadingClubAkahuConnection || sessionController.needsAkahuConnection);

  if (loading || !record) return null;

  return (
    <Modal title={title} onClose={controller.actionCancel.bind(controller)} portal>
      <Form name="payment-method-form">
        {isAkahu && (
          <div className="mb-4">
            <ClubAkahuSetupBanner
              connection={clubAkahuConnection}
              loading={loadingClubAkahuConnection}
              connecting={startingAkahuOnboarding}
              onConnect={() => sessionController.actionStartAkahuOnboarding()}
              showSupportedBanks
            />
          </div>
        )}
        <fieldset
          disabled={akahuBlocked}
          className={akahuBlocked ? "pointer-events-none select-none opacity-40" : undefined}
          aria-hidden={akahuBlocked}
        >
          <ModalFormFieldset fields={basicFields} />
          {configFields.length > 0 && (
            <>
              <h3 className="mb-2 mt-4 text-lg font-medium">Configuration</h3>
              <ModalFormFieldset fields={configFields} />
            </>
          )}
        </fieldset>
        <FormButtons showDelete={false} showCancel disabled={akahuBlocked} />
      </Form>
    </Modal>
  );
};

export default TournamentPaymentMethodFormController.scope(ApplicationView(TournamentPaymentMethodForm));
